import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Loading as Family, Waiting as Region } from '@beyond-js/ui/react';
import { useI18n } from '@cdn/i18n';
import { TextLink } from '@cdn/navigation';
import styles from './styles.module.css';
import { Unavailable } from './unavailable';

/**
 * The family's announced loading line, with the family's label in the selected language: for the document's
 * opening waits, before the signed-in page exists; a page or a region waits with `Waiting`.
 */
export function Loading({ label }: { label?: string }) {
	const { locale } = useI18n();
	return (
		<div className="py-6">
			<Family label={label ?? Family.labels[locale.code === 'es' ? 'es' : 'en']} />
		</div>
	);
}

/** The shape of a region's wait: how many rows of what kind, and whether the region already draws a frame */
export interface Shape {
	rows?: number;
	kind?: 'list' | 'table' | 'lines';
	/** `false` inside a region that is already framed: a dialog, a card or a raised panel */
	frame?: boolean;
	label?: string;
}

/**
 * A region's first read (LOAD-01): the family's wait in the content's shape, with an announced "Loading…" in the
 * selected language, inside the region it belongs to; its heading and actions stay drawn above it. The block is
 * busy until the content replaces it.
 */
export function Waiting({ rows = 1, kind = 'list', frame = true, label }: Shape) {
	const { locale } = useI18n();
	return (
		<div className="min-w-0" aria-busy="true">
			<Region rows={rows} kind={kind} frame={frame} label={label} language={locale.code === 'es' ? 'es' : 'en'} />
		</div>
	);
}

/**
 * One line of a value still being read, held in its place on a line of text (a title, a count, a fact): the
 * family's skeleton, so the opening and a wait's 300 ms grace see it as they see a region's wait (D185). Only its
 * `label`, when given, is said to assistive technology.
 */
export function Placeholder({ width, label }: { width: string; label?: string }) {
	return (
		<>
			<span className="bui-skeleton" style={{ display: 'inline-grid', padding: 0, verticalAlign: 'middle' }} aria-hidden="true">
				<span className="bui-skeleton-line" style={{ width }} />
			</span>
			{label && <span className="sr-only">{label}</span>}
		</>
	);
}

/**
 * An empty list is an invitation to act: it says what belongs here and offers the action, as the family's
 * empty state draws it. Its title leads it, its capitals one inset from the border, and its action is the empty
 * state's own row, whose edges keep that inset too (D184).
 */
export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
	return (
		<div className={`bui-empty ${styles.empty}`}>
			<h3 className="bui-empty-title text-title-3">{title}</h3>
			{children && <div className="bui-empty-body">{children}</div>}
			{action && <div className="bui-empty-actions mt-2">{action}</div>}
		</div>
	);
}

/**
 * The single state for a resource that does not exist and for one the account cannot see. The interface never
 * tells the two apart, exactly as the service answers both with `NOT_FOUND`. It is the family's "not available
 * here" (decision D06), as every product answers an address it cannot open: what happened, who can change it,
 * and always a way on — the given action, or the console's start, which continues in the organization used
 * last. Its heading is the page's title, so focus lands on it after the navigation (see the shell).
 */
export function NotFound({ action }: { action?: ReactNode }) {
	const { t } = useI18n();
	const host = useRef<HTMLDivElement>(null);

	// The shared component draws the heading; it is marked here, before the shell looks for the page's title
	useLayoutEffect(() => {
		const heading = host.current?.querySelector<HTMLElement>('.bui-unavailable-title');
		if (!heading) return;
		heading.tabIndex = -1;
		heading.setAttribute('data-page-title', '');
	}, []);

	return (
		<div ref={host} className="bui-form-width py-4">
			<Unavailable
				kind="access"
				title={t('ui.notfound.title')}
				reason={t('ui.notfound.text')}
				owner={t('ui.notfound.owner')}
				action={action ?? <TextLink to="/">{t('ui.notfound.start')}</TextLink>}
			/>
		</div>
	);
}
